import { Injectable } from "@angular/core";
import { ADVISOR_CONSULTATION_CONTEXT } from "@shared/constants/localstorage.constants";
import { ADVISOR_SSO_CTX } from "@shared/constants/msal.constants";
import { AdvisorConsultationContext } from "@shared/models/advisor-consultation-context.model";
import { MsalAuthenticationService } from "./msal-authentication.service";

const CTX_TTL_MS = 15 * 60 * 1000; // 15 minutes

@Injectable({
  providedIn: "root",
})
export class ConsultationContextService {
  constructor(private readonly msalAuthenticationService: MsalAuthenticationService) {}

  captureFromUrlAndClean(): void {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(ADVISOR_SSO_CTX);

    if (!raw) {
      return;
    }

    try {
      const decoded = atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
      const payload = JSON.parse(decoded) as Partial<AdvisorConsultationContext>;

      if (!payload.customerId || !payload.customerEmail) {
        console.warn("[ConsultationContext] Contexte invalide : champs obligatoires manquants");
        return;
      }

      const context: AdvisorConsultationContext = {
        customerId: payload.customerId,
        customerEmail: payload.customerEmail,
        policyId: payload.policyId,
        expiresAt: Date.now() + CTX_TTL_MS,
      };

      sessionStorage.setItem(ADVISOR_CONSULTATION_CONTEXT, JSON.stringify(context));

      // Important : marquer le flux SSO comme en cours pour que le guard le détecte
      this.msalAuthenticationService.markPending();
    } catch {
      console.warn("[ConsultationContext] Impossible de décoder le paramètre ctx");
    } finally {
      this.cleanCtxFromUrl();
    }
  }

  getContext(): AdvisorConsultationContext | null {
    const raw = sessionStorage.getItem(ADVISOR_CONSULTATION_CONTEXT);
    if (!raw) {
      return null;
    }

    const context = JSON.parse(raw) as AdvisorConsultationContext;

    if (Date.now() > context.expiresAt) {
      this.clearContext();
      return null;
    }

    // TTL: while the advisor is active, we consider the context valid and we refresh its expiration time at each access.
    const refreshedContext: AdvisorConsultationContext = {
      ...context,
      expiresAt: Date.now() + CTX_TTL_MS,
    };
    sessionStorage.setItem(ADVISOR_CONSULTATION_CONTEXT, JSON.stringify(refreshedContext));

    return refreshedContext;
  }

  clearContext(): void {
    sessionStorage.removeItem(ADVISOR_CONSULTATION_CONTEXT);
  }

  private cleanCtxFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(ADVISOR_SSO_CTX);
    window.history.replaceState({}, "", url.toString());
  }
}
